import { useCallback, useRef } from "react";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import type { components } from "@/lib/api/schema";

export const overviewQuery = queryOptions({
  queryKey: ["account", "overview"],
  queryFn: async () => {
    const { data, error } = await api.GET("/account/overview");
    if (error || !data) throw error ?? new Error("No account data");
    return data;
  },
});

export const sessionsQuery = queryOptions({
  queryKey: ["account", "sessions"],
  queryFn: async () => {
    const { data, error } = await api.GET("/account/sessions");
    if (error || !data) throw error ?? new Error("No session data");
    return data;
  },
});

export const sessionDetailQuery = (clientId: string, sessionId: number) =>
  queryOptions({
    queryKey: ["account", "sessions", clientId, sessionId],
    queryFn: async () => {
      const { data, error } = await api.GET(
        "/account/sessions/{client_id}/{session_id}",
        { params: { path: { client_id: clientId, session_id: sessionId } } },
      );
      if (error || !data) throw error ?? new Error("Session was not returned");
      return data;
    },
  });

export const productsQuery = queryOptions({
  queryKey: ["account", "products"],
  queryFn: async () => {
    const { data, error } = await api.GET("/account/products");
    if (error || !data) throw error ?? new Error("No product data");
    return data;
  },
});

export function computeAvatarRefetchInterval(
  view: components["schemas"]["AvatarView"] | null | undefined,
  now: number,
): number | false {
  if (!view) return false;
  const expiresAt = Date.parse(view.expires_at);
  if (!Number.isFinite(expiresAt)) return false;
  return Math.max(expiresAt - now - 60_000, 30_000);
}

export const avatarQuery = queryOptions({
  queryKey: ["account", "avatar"],
  queryFn: async () => {
    const { data, error, response } = await api.GET("/profile/avatar");
    if (response.status === 404) return null;
    if (error || !data) throw error ?? new Error("No avatar data");
    return data;
  },
  refetchInterval: (query) =>
    computeAvatarRefetchInterval(
      query.state.data as
        components["schemas"]["AvatarView"] | null | undefined,
      Date.now(),
    ),
});

export function useAvatarImageErrorRecovery() {
  const client = useQueryClient();
  const lastAttempt = useRef<{ key: string; at: number } | null>(null);
  return useCallback(
    (version?: string) => {
      const now = Date.now();
      const key = version ?? "";
      if (
        lastAttempt.current?.key === key &&
        now - lastAttempt.current.at < 10_000
      ) {
        return;
      }
      lastAttempt.current = { key, at: now };
      void client.invalidateQueries({ queryKey: ["account", "avatar"] });
    },
    [client],
  );
}
